import { memo, type CSSProperties } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import { type SpliceNode as TSpliceNode } from '../store';

// 8 connection points (2 per side) so a junction can fan out to many wires.
// ids match SPLICE_HANDLE_IDS in store.ts (the first four keep legacy names).
const HANDLES: { id: string; position: Position; style: CSSProperties }[] = [
  { id: 'p-top', position: Position.Top, style: { left: '33%' } },
  { id: 'p-top-2', position: Position.Top, style: { left: '67%' } },
  { id: 'p-right', position: Position.Right, style: { top: '33%' } },
  { id: 'p-right-2', position: Position.Right, style: { top: '67%' } },
  { id: 'p-bottom', position: Position.Bottom, style: { left: '33%' } },
  { id: 'p-bottom-2', position: Position.Bottom, style: { left: '67%' } },
  { id: 'p-left', position: Position.Left, style: { top: '33%' } },
  { id: 'p-left-2', position: Position.Left, style: { top: '67%' } },
];

function SpliceNodeImpl({ data, selected }: NodeProps<TSpliceNode>) {
  const accent = data.kind === 'junction' ? '#2563eb' : '#f59e0b';

  return (
    <div
      title="Splice / junction point (8 connection points)"
      style={{
        position: 'relative',
        width: 40,
        height: 40,
        display: 'grid',
        placeItems: 'center',
        cursor: 'move',
      }}
    >
      {HANDLES.map((h) => (
        <span key={h.id}>
          <Handle type="source" id={h.id} position={h.position} isConnectableStart isConnectableEnd style={{ ...handleStyle, ...h.style }} />
          <Handle type="target" id={h.id} position={h.position} isConnectableStart isConnectableEnd style={{ ...handleStyle, ...h.style, opacity: 0 }} />
        </span>
      ))}

      <div
        style={{
          width: 18,
          height: 18,
          borderRadius: 5,
          transform: 'rotate(45deg)',
          background: 'var(--panel)',
          border: `2px solid ${accent}`,
          boxShadow: selected ? `0 0 0 4px color-mix(in srgb, ${accent} 22%, transparent)` : 'var(--shadow-sm)',
        }}
      />
      <div
        style={{
          position: 'absolute',
          top: 8,
          left: 0,
          width: 40,
          textAlign: 'center',
          fontSize: 8.5,
          fontWeight: 900,
          color: 'var(--text)',
          fontFamily: 'ui-monospace, Consolas, monospace',
          pointerEvents: 'none',
        }}
      >
        {data.label}
      </div>
    </div>
  );
}

const handleStyle = {
  width: 8,
  height: 8,
  background: '#fff',
  border: '2px solid #475569',
  borderRadius: 999,
};

export const SpliceNode = memo(SpliceNodeImpl);
